import { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { SyncForecastCompletion } from './ImportPage';

// Flexible, case-insensitive column resolution — the export's column layout
// drifted at least 8 times across ~7 months of weekly sheets (numeric roll-ups
// early on, narrative fields later). Exact-match candidates are tried first,
// then a substring fallback so renamed-but-similar headers still resolve.
const FIELD_CANDIDATES = {
  reference:                 ['av reference'],
  status:                    ['status'],
  end_date:                  ['end date'],
  forecast_completion_date:  ['forecast completion date'],
  owner:                     ['owner'],
  achievements:              ['achievements since last report', 'achievements'],
  planned_next:              ['planned by next report', 'planned'],
  rr_actions:                ['rr actions'],
  rr_comment:                ['rr comment'],
  dependency_delay:          ['dependency delay'],
  dependency_comment:        ['dependency comment'],
  risks:                     ['risks'],
};

function resolveColumns(headerRow) {
  const norm = headerRow.map(h => String(h || '').trim().toLowerCase());
  const idx = {};
  for (const [field, candidates] of Object.entries(FIELD_CANDIDATES)) {
    let found = -1;
    for (const c of candidates) { found = norm.indexOf(c); if (found >= 0) break; }
    if (found < 0) {
      for (const c of candidates) {
        found = norm.findIndex(h => h.includes(c));
        if (found >= 0) break;
      }
    }
    idx[field] = found;
  }
  return idx;
}

// Sheet names are "DD.MM.YYYY" (the week's Friday report date, used here as the
// snapshot's week_commencing key) — one seen in the wild was "07.01.2025" which
// is almost certainly a typo for 07.01.2026; parsed literally either way since
// silently "fixing" a date in someone else's data is worse than flagging it.
function sheetNameToIso(name) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(name.trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
}

// END DATE / FORECAST COMPLETION DATE cells come back from XLSX as UK-formatted
// display strings (DD/MM/YYYY), not ISO — `new Date("31/01/2024")` in the app's
// formatDate() is Invalid Date. Convert to ISO; pass through if already ISO;
// otherwise leave null rather than guess (the original text still lives in raw_json).
function parseUkDate(str) {
  if (!str) return null;
  const s = str.trim();
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) { const [, dd, mm, yyyy] = m; return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`; }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

function parseWorkbook(wb, filename) {
  const rows = [];
  const warnings = [];
  let unparsedDates = 0;
  for (const sheetName of wb.SheetNames) {
    if (/comment/i.test(sheetName)) continue; // client-facing variant duplicates the primary sheet's week
    const weekCommencing = sheetNameToIso(sheetName);
    if (!weekCommencing) { warnings.push(`Skipped sheet "${sheetName}" — name isn't a DD.MM.YYYY date.`); continue; }
    const ws = wb.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
    if (!raw.length) continue;
    const idx = resolveColumns(raw[0]);
    if (idx.reference < 0) { warnings.push(`Skipped sheet "${sheetName}" — no "AV Reference" column found.`); continue; }
    for (const r of raw.slice(1)) {
      const reference = String(r[idx.reference] || '').trim();
      if (!/^AV-?\s*\d+/i.test(reference)) continue;
      const cleanRef = reference.replace(/^AV-?\s*/i, 'AV-').replace(/\s+/g, '');
      const get = (field) => (idx[field] >= 0 ? String(r[idx[field]] || '').trim() || null : null);
      const rawObj = {};
      raw[0].forEach((h, i) => { if (h) rawObj[h] = r[i] ?? ''; });
      const rawEndDate = get('end_date'), rawForecastDate = get('forecast_completion_date');
      const endDate = parseUkDate(rawEndDate), forecastDate = parseUkDate(rawForecastDate);
      if (rawEndDate && !endDate) unparsedDates++;
      if (rawForecastDate && !forecastDate) unparsedDates++;
      rows.push({
        reference: cleanRef, week_commencing: weekCommencing,
        status: get('status'), end_date: endDate, forecast_completion_date: forecastDate,
        owner: get('owner'), achievements: get('achievements'), planned_next: get('planned_next'),
        rr_actions: get('rr_actions'), rr_comment: get('rr_comment'),
        dependency_delay: get('dependency_delay'), dependency_comment: get('dependency_comment'), risks: get('risks'),
        raw: rawObj, source_file: filename,
      });
    }
  }
  if (unparsedDates > 0) warnings.push(`${unparsedDates} end/forecast date value(s) didn't match DD/MM/YYYY or YYYY-MM-DD and were left blank — the original text is still kept in each row's raw data.`);
  return { rows, warnings };
}

const IMPORT_CHUNK = 200;

const DIFF_FIELD_LABELS = {
  delivery_status: 'Delivery status',
};

export default function ZohoCutoverImportPage() {
  const { toast } = useToast();
  const [parsing, setParsing]   = useState(false);
  const [parsed, setParsed]     = useState(null);   // { rows, warnings }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { imported, unmatched_references }
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]   = useState(null);   // { total_legacy_sows, diffs }
  const [selectedDiffs, setSelectedDiffs] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true); setImportResult(null); setPreview(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        setParsed(parseWorkbook(wb, file.name));
      } catch (err) {
        toast.error('Could not read that file — ' + (err.message || 'unknown error'));
        setParsed(null);
      }
      setParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const runImport = async () => {
    if (!parsed?.rows?.length) return;
    setImporting(true);
    let imported = 0;
    const unmatched = new Set();
    try {
      for (let i = 0; i < parsed.rows.length; i += IMPORT_CHUNK) {
        const chunk = parsed.rows.slice(i, i + IMPORT_CHUNK);
        const r = await api.post('sow-legacy-history/import', { rows: chunk });
        imported += r.imported || 0;
        (r.unmatched_references || []).forEach(ref => unmatched.add(ref));
      }
      setImportResult({ imported, unmatched_references: [...unmatched].sort() });
      toast.success(`Imported ${imported} weekly snapshot rows.`);
    } catch (err) {
      toast.error('Import failed: ' + (err.message || 'unknown error'));
    }
    setImporting(false);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.get('sow-legacy-history/apply-preview');
      setPreview(r);
      setSelectedDiffs(new Set((r.diffs || []).map((_, i) => i)));
    } catch (err) {
      toast.error('Preview failed: ' + (err.message || 'unknown error'));
    }
    setPreviewing(false);
  };

  const toggleDiff = (i) => {
    setSelectedDiffs(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const applySelected = async () => {
    const diffs = (preview?.diffs || []).filter((_, i) => selectedDiffs.has(i));
    if (!diffs.length) return;
    if (!window.confirm(`Apply ${diffs.length} change${diffs.length === 1 ? '' : 's'} to live projects? Each will be logged with a before/after audit note tagged "Zoho cutover".`)) return;
    setApplying(true);
    try {
      const r = await api.post('sow-legacy-history/apply', { diffs });
      const ok = r.results.filter(x => x.success).length;
      const fail = r.results.filter(x => !x.success).length;
      toast[fail ? 'error' : 'success'](`Applied ${ok} change${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`);
      await runPreview();
    } catch (err) {
      toast.error('Apply failed: ' + (err.message || 'unknown error'));
    }
    setApplying(false);
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">Zoho Cutover Import</h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          One-off go-live migration. Step 1: import the weekly Zoho delivery-report export as read-only history
          (visible per-SOW via the "Legacy History" button). Step 2: preview and optionally sync the latest
          snapshot onto the live project — every change is logged with a before/after note tagged "Zoho cutover".
        </div>
      </div>

      {/* Step 1 — import */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4">
        <div className="text-[10px] font-bold text-navy mb-2">Step 1 — Import weekly history</div>
        <input type="file" accept=".xlsx" onChange={onFile} disabled={parsing || importing} className="text-[10px]" />
        {parsing && <div className="text-[9px] text-gray-400 mt-2">Parsing…</div>}
        {parsed && (
          <div className="mt-3">
            <div className="text-[9px] text-gray-600">
              Found <strong>{parsed.rows.length}</strong> weekly rows across {new Set(parsed.rows.map(r => r.week_commencing)).size} weeks,{' '}
              {new Set(parsed.rows.map(r => r.reference)).size} distinct SOW references.
            </div>
            {parsed.warnings.length > 0 && (
              <ul className="text-[8px] text-amber-600 mt-1 list-disc pl-4">
                {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <button onClick={runImport} disabled={importing || !parsed.rows.length}
              className="mt-2 px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
              {importing ? 'Importing…' : `Import ${parsed.rows.length} rows`}
            </button>
          </div>
        )}
        {importResult && (
          <div className="mt-3 text-[9px] text-gray-600">
            Imported {importResult.imported} rows.
            {importResult.unmatched_references.length > 0 && (
              <div className="text-amber-600 mt-1">
                {importResult.unmatched_references.length} reference{importResult.unmatched_references.length === 1 ? '' : 's'} not found as a SOW in this system (skipped): {importResult.unmatched_references.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — preview + apply */}
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold text-navy">Step 2 — Sync latest snapshot onto live projects</div>
          <button onClick={runPreview} disabled={previewing}
            className="px-2 py-1 text-[9px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40">
            {previewing ? 'Loading…' : preview ? 'Refresh preview' : 'Preview changes'}
          </button>
        </div>
        {preview && (
          <>
            <div className="text-[9px] text-gray-600 mb-2">
              {preview.total_legacy_sows} SOW{preview.total_legacy_sows === 1 ? '' : 's'} have legacy history.{' '}
              {preview.diffs.length} field{preview.diffs.length === 1 ? '' : 's'} differ from the live project and would change.
            </div>
            {preview.diffs.length > 0 && (
              <>
                <div className="overflow-x-auto"><table className="w-full mb-2">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-2 py-1 w-6" />
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Field</th>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Current</th>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Proposed (from Zoho)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.diffs.map((d, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-2 py-1"><input type="checkbox" checked={selectedDiffs.has(i)} onChange={() => toggleDiff(i)} /></td>
                        <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{d.reference}</td>
                        <td className="px-2 py-1 text-[9px] text-gray-600">{DIFF_FIELD_LABELS[d.field] || d.field}{d.milestone_code ? ` (${d.milestone_code})` : ''}</td>
                        <td className="px-2 py-1 text-[9px] text-gray-500">{d.current_value}</td>
                        <td className="px-2 py-1 text-[9px] text-emerald-700 font-semibold">{d.proposed_value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
                <button onClick={applySelected} disabled={applying || !selectedDiffs.size}
                  className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
                  {applying ? 'Applying…' : `Apply ${selectedDiffs.size} selected`}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Repair — earlier versions of this tool wrote forecast_completion_date
          straight from the Zoho export before UK-date parsing was fixed, which
          could have left sows.forecast_completion holding a non-ISO string
          (e.g. "19/06/2026" instead of "2026-06-19"). Recomputing it as the max
          forecast date across each SOW's active milestones — its correct,
          derived definition — repairs that regardless of which SOWs were hit. */}
      <div className="bg-white border border-gray-200 rounded p-4 mt-4">
        <div className="text-[10px] font-bold text-navy mb-2">Repair — recompute SOW forecast completion dates</div>
        <div className="text-[9px] text-gray-500 mb-3">
          If you ran the sync in Step 2 before this date-parsing fix went live, sows.forecast_completion may hold a
          bad value from back then. This recomputes it correctly (max forecast date across each SOW's active
          milestones) — safe to run any time, including if nothing is actually wrong.
        </div>
        <SyncForecastCompletion />
      </div>
    </div>
  );
}
